import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import * as Icons from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { toast } from "sonner";

/**
 * Edit a realtor — required to feed the branded outro card.
 */
export default function RealtorEditModal({ realtor, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [brokerage, setBrokerage] = useState("");
  const [website, setWebsite] = useState("");
  const [chatbotUrl, setChatbotUrl] = useState("");
  const [headshotId, setHeadshotId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  // Dashboard credibility fields
  const [tagline, setTagline] = useState("");
  const [salesCount, setSalesCount] = useState("");
  const [yearsExp, setYearsExp] = useState("");
  const [testimonials, setTestimonials] = useState([]);
  // Availability (showing slots)
  const [availDuration, setAvailDuration] = useState(30);
  const [availBuffer, setAvailBuffer] = useState(10);
  const [availTimezone, setAvailTimezone] = useState("UTC");
  const [availWindows, setAvailWindows] = useState([]);
  const fileRef = useRef();

  useEffect(() => {
    if (!realtor) return;
    setName(realtor.name || "");
    setEmail(realtor.email || "");
    setPhone(realtor.phone || "");
    setBrokerage(realtor.brokerage_name || "");
    setWebsite(realtor.website || "");
    setChatbotUrl(realtor.chatbot_embed_url || "");
    setHeadshotId(realtor.headshot_image_id || null);
    setTagline(realtor.tagline || "");
    setSalesCount(realtor.sales_count_90d ? String(realtor.sales_count_90d) : "");
    setYearsExp(realtor.years_experience ? String(realtor.years_experience) : "");
    setTestimonials(Array.isArray(realtor.testimonials) ? realtor.testimonials : []);
    const av = realtor.availability || {};
    setAvailDuration(av.duration_minutes || 30);
    setAvailBuffer(av.buffer_minutes ?? 10);
    setAvailTimezone(av.timezone || (Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"));
    setAvailWindows(Array.isArray(av.windows) ? av.windows : []);
  }, [realtor]);

  if (!realtor) return null;

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setHeadshotId(data.id);
      toast.success("Headshot uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    try {
      await api.patch(`/realtors/${realtor.id}`, {
        name,
        email,
        phone,
        brokerage_name: brokerage,
        website,
        headshot_image_id: headshotId,
        chatbot_embed_url: chatbotUrl.trim(),
      });
      // (different endpoint, different validation; keeps the contract clean)
      await api.patch(`/realtors/${realtor.id}/profile`, {
        tagline,
        sales_count_90d: salesCount === "" ? null : Number(salesCount),
        years_experience: yearsExp === "" ? null : Number(yearsExp),
        testimonials: testimonials.filter((t) => t && (t.quote || "").trim()),
      });
      // Save availability separately
      await api.patch(`/realtors/${realtor.id}/availability`, {
        duration_minutes: Number(availDuration) || 30,
        buffer_minutes: Number(availBuffer) || 0,
        timezone: availTimezone || "UTC",
        windows: (availWindows || []).filter(
          (w) => w && w.day !== undefined && w.start && w.end
        ),
      });
      toast.success("Saved");
      onSaved?.();
      onClose();
    } catch {
      toast.error("Save failed");
    }
  };

  const openPreview = async () => {
    // Save current edits first so the preview reflects what's in the form
    setPreviewing(true);
    try {
      await api.patch(`/realtors/${realtor.id}`, {
        name,
        email,
        phone,
        brokerage_name: brokerage,
        website,
        headshot_image_id: headshotId,
      });
      const { data } = await api.get(`/realtors/${realtor.id}/outro-preview`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
    } catch {
      toast.error("Could not render preview");
    } finally {
      setPreviewing(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const copyDashboardLink = async () => {
    try {
      const { data } = await api.get(`/realtors/${realtor.id}/dashboard-token`);
      const url = `${window.location.origin}/r/${data.token}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Dashboard link copied", { description: url });
      } catch {
        window.prompt("Copy this dashboard link:", url);
      }
    } catch {
      toast.error("Could not generate dashboard link");
    }
  };

  const emailDashboardLink = async () => {
    try {
      const { data } = await api.get(`/realtors/${realtor.id}/dashboard-token`);
      const url = `${window.location.origin}/r/${data.token}`;
      const firstName = (name || realtor.name || "there").trim().split(/\s+/)[0];
      const subject = `Your private listing dashboard`;
      const body =
        `Hi ${firstName},\n\n` +
        `Here's your private dashboard — it tracks views on every listing reel I've shared for you, live:\n\n` +
        `${url}\n\n` +
        `Bookmark it. No login needed. New reels will show up automatically.\n\n` +
        `Cheers`;
      const mailto = `mailto:${encodeURIComponent(email || "")}?subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(body)}`;
      window.location.href = mailto;
    } catch {
      toast.error("Could not generate dashboard link");
    }
  };

  // When previewing, show ONLY the full-screen preview (not the edit modal underneath)
  if (previewUrl) {
    return createPortal(
      <div
        className="fixed inset-0 z-[9999] bg-black flex items-center justify-center p-6"
        onClick={closePreview}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            closePreview();
          }}
          className="absolute top-4 right-4 text-zinc-400 hover:text-white p-2"
          data-testid="outro-preview-close"
        >
          <Icons.X size={18} />
        </button>
        <div className="text-center" onClick={(e) => e.stopPropagation()}>
          <img
            src={previewUrl}
            alt="Outro card preview"
            className="max-w-full max-h-[85vh] rounded-lg border border-white/10"
            data-testid="outro-preview-image"
          />
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mt-4">
            1920 × 1080 · Click anywhere to close
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9998] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg text-white">Realtor Profile</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <Icons.X size={16} />
          </button>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div
            onClick={() => fileRef.current?.click()}
            className="relative w-20 h-20 rounded-full border-2 border-dashed border-white/20 hover:border-white/40 cursor-pointer overflow-hidden bg-black/40 flex items-center justify-center"
            data-testid="realtor-headshot"
          >
            {headshotId ? (
              <img
                src={mediaUrl(`/api/public/images/${headshotId}`)}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <Icons.User size={20} className="text-zinc-600" />
            )}
            {uploading && (
              <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                <Icons.Loader2 size={16} className="animate-spin" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="text-xs text-white">Headshot</div>
            <div className="text-[10px] text-zinc-500 mt-0.5">
              Used on the branded outro card.
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={upload}
          />
        </div>

        <div className="space-y-3">
          <Field label="Name" value={name} onChange={setName} testid="rname" />
          <Field
            label="Brokerage name"
            value={brokerage}
            onChange={setBrokerage}
            placeholder="(falls back to company name if blank)"
            testid="rbroker"
          />
          <Field
            label="Phone"
            value={phone}
            onChange={setPhone}
            testid="rphone"
          />
          <Field
            label="Email"
            value={email}
            onChange={setEmail}
            testid="remail"
          />
          <Field
            label="Website"
            value={website}
            onChange={setWebsite}
            testid="rweb"
          />
        </div>

        {/* AI Chatbot — Magicalnodes (or any iframe-embeddable bot).
            When set, a floating chat bubble appears on every share page
            and dashboard for this realtor so buyers can self-serve. */}
        <div className="mt-6 pt-5 border-t border-white/5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3 flex items-center gap-2">
            <Icons.MessageCircle size={11} />
            AI chatbot for buyers
          </div>
          <p className="text-[11px] text-zinc-600 mb-3 leading-relaxed">
            Paste your Magicalnodes embed <span className="text-zinc-400">src URL</span> below.
            A floating <span className="text-zinc-400">"Ask AI"</span> bubble will appear on every
            share page &amp; dashboard for this realtor — buyers can ask questions
            24/7 without waiting for a callback.
          </p>
          <Field
            label="Chatbot embed URL"
            value={chatbotUrl}
            onChange={setChatbotUrl}
            placeholder="https://magicalnodes.com/embed/<id>?key=<key>"
            testid="r-chatbot-url"
          />
          {chatbotUrl && !/^https?:\/\//i.test(chatbotUrl) && (
            <div className="text-[10px] text-amber-400/80 mt-1.5 flex items-start gap-1.5">
              <Icons.AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />
              URL should start with https://
            </div>
          )}
          {chatbotUrl && /^https?:\/\//i.test(chatbotUrl) && (
            <details className="mt-3 text-[11px] text-zinc-500">
              <summary className="cursor-pointer hover:text-zinc-300 inline-flex items-center gap-1.5">
                <Icons.Code2 size={11} />
                Context params automatically appended
              </summary>
              <div className="mt-2 bg-black/40 border border-white/5 rounded-md p-3 space-y-2 leading-relaxed">
                <div className="text-zinc-400 text-[10px] tracking-[0.2em] uppercase">
                  On share pages (one listing)
                </div>
                <code className="block text-[10px] text-zinc-400 font-mono break-all">
                  page, share_token, listing_address, listing_url,
                  listing_price, listing_currency, listing_beds,
                  listing_baths, listing_sqft, realtor_name,
                  realtor_phone, realtor_email
                </code>
                <div className="text-zinc-400 text-[10px] tracking-[0.2em] uppercase pt-1">
                  On the realtor dashboard (all listings)
                </div>
                <code className="block text-[10px] text-zinc-400 font-mono break-all">
                  page, realtor_name, realtor_phone, realtor_email,
                  realtor_dashboard_token, listing_count, listing_addresses
                </code>
                <div className="text-zinc-600 text-[10px] pt-1">
                  Configure your Magicalnodes bot to read these query
                  params so it can greet visitors with the listing they're
                  currently viewing.
                </div>
              </div>
            </details>
          )}
        </div>

        {/* Dashboard credibility — shown atop the realtor's public dashboard */}
        <div className="mt-6 pt-5 border-t border-white/5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3 flex items-center gap-2">
            <Icons.Sparkles size={11} />
            Dashboard credibility
          </div>
          <div className="space-y-3">
            <Field
              label="Tagline"
              value={tagline}
              onChange={setTagline}
              placeholder='e.g. "Closing 2x faster than the local average"'
              testid="r-tagline"
            />
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Sales last 90 days"
                value={salesCount}
                onChange={setSalesCount}
                placeholder="12"
                testid="r-sales-count"
              />
              <Field
                label="Years experience"
                value={yearsExp}
                onChange={setYearsExp}
                placeholder="8"
                testid="r-years-exp"
              />
            </div>
            <TestimonialsEditor
              testimonials={testimonials}
              onChange={setTestimonials}
            />
          </div>
        </div>

        {/* Showings — weekly availability windows */}
        <div className="mt-6 pt-5 border-t border-white/5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3 flex items-center gap-2">
            <Icons.CalendarCheck size={11} />
            Showings (slot picker)
          </div>
          <p className="text-[11px] text-zinc-600 mb-3">
            Define when you can show listings. Viewers will pick from these
            slots inside the "Book a viewing" CTA on every reel.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            <Field
              label="Duration (min)"
              value={String(availDuration)}
              onChange={(v) => setAvailDuration(v)}
              testid="avail-duration"
            />
            <Field
              label="Buffer (min)"
              value={String(availBuffer)}
              onChange={(v) => setAvailBuffer(v)}
              testid="avail-buffer"
            />
            <Field
              label="Timezone"
              value={availTimezone}
              onChange={setAvailTimezone}
              testid="avail-timezone"
            />
          </div>
          <AvailabilityWindowsEditor
            windows={availWindows}
            onChange={setAvailWindows}
          />
        </div>

        {/* Utility actions — each button on its own for clarity */}
        <div className="grid grid-cols-3 gap-2 mt-6 pt-5 border-t border-white/5">
          <button
            onClick={openPreview}
            disabled={previewing}
            className="flex items-center justify-center gap-1.5 text-xs text-zinc-300 border border-white/15 rounded-lg px-3 py-2.5 hover:bg-white/5 disabled:opacity-50 whitespace-nowrap"
            data-testid="outro-preview-btn"
          >
            {previewing ? (
              <Icons.Loader2 size={13} className="animate-spin" />
            ) : (
              <Icons.Eye size={13} />
            )}
            Preview outro
          </button>
          <button
            onClick={copyDashboardLink}
            className="flex items-center justify-center gap-1.5 text-xs text-zinc-300 border border-white/15 rounded-lg px-3 py-2.5 hover:bg-white/5 whitespace-nowrap"
            title="Share a private link with this realtor so they can view their listing reel stats"
            data-testid="realtor-copy-dashboard-link"
          >
            <Icons.Link2 size={13} />
            Copy dashboard link
          </button>
          <button
            onClick={emailDashboardLink}
            disabled={!email}
            className="flex items-center justify-center gap-1.5 text-xs text-zinc-300 border border-white/15 rounded-lg px-3 py-2.5 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            title={
              email
                ? "Open your email client with a pre-filled intro + dashboard link"
                : "Add an email address first"
            }
            data-testid="realtor-email-dashboard-link"
          >
            <Icons.Mail size={13} />
            Email dashboard
          </button>
        </div>

        {/* Primary actions */}
        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="text-sm text-zinc-400 px-4 py-2.5 hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={save}
            className="bg-white text-black text-sm font-medium rounded-lg px-6 py-2.5 hover:bg-zinc-200"
            data-testid="realtor-save"
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">
        {label}
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-700 focus:outline-none focus:border-white/30"
        data-testid={testid}
      />
    </div>
  );
}

function TestimonialsEditor({ testimonials, onChange }) {
  const update = (idx, key, value) => {
    const next = [...testimonials];
    next[idx] = { ...(next[idx] || {}), [key]: value };
    onChange(next);
  };
  const remove = (idx) => {
    const next = testimonials.filter((_, i) => i !== idx);
    onChange(next);
  };
  const add = () => {
    if (testimonials.length >= 6) return;
    onChange([...(testimonials || []), { quote: "", author: "", role: "" }]);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">
          Testimonials ({testimonials.length}/6)
        </div>
        {testimonials.length < 6 && (
          <button
            type="button"
            onClick={add}
            className="text-[11px] text-zinc-400 hover:text-white inline-flex items-center gap-1"
            data-testid="testimonial-add"
          >
            <Icons.Plus size={11} />
            Add
          </button>
        )}
      </div>
      {testimonials.length === 0 && (
        <div className="text-[11px] text-zinc-600 italic px-3 py-3 border border-dashed border-white/10 rounded-md">
          No testimonials yet. Add quotes from past sellers — they'll show on
          your public dashboard as a portfolio carousel.
        </div>
      )}
      <div className="space-y-2">
        {testimonials.map((t, i) => (
          <div
            key={i}
            className="bg-white/[0.03] border border-white/10 rounded-lg p-3 space-y-2"
            data-testid={`testimonial-row-${i}`}
          >
            <textarea
              value={t.quote || ""}
              onChange={(e) => update(i, "quote", e.target.value)}
              placeholder="“They were brilliant — sold our place in 8 days.”"
              rows={2}
              className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-700 focus:outline-none focus:border-white/30"
              data-testid={`testimonial-${i}-quote`}
            />
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
              <input
                value={t.author || ""}
                onChange={(e) => update(i, "author", e.target.value)}
                placeholder="Author (e.g. The Robinsons)"
                className="bg-black/40 border border-white/10 rounded-md px-3 py-1.5 text-xs text-white placeholder:text-zinc-700 focus:outline-none focus:border-white/30"
                data-testid={`testimonial-${i}-author`}
              />
              <input
                value={t.role || ""}
                onChange={(e) => update(i, "role", e.target.value)}
                placeholder='Role (e.g. "Sold Mar 2026")'
                className="bg-black/40 border border-white/10 rounded-md px-3 py-1.5 text-xs text-white placeholder:text-zinc-700 focus:outline-none focus:border-white/30"
                data-testid={`testimonial-${i}-role`}
              />
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-[11px] text-zinc-500 hover:text-red-400 px-2 py-1.5 inline-flex items-center gap-1"
                data-testid={`testimonial-${i}-remove`}
              >
                <Icons.X size={11} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function AvailabilityWindowsEditor({ windows, onChange }) {
  const update = (idx, key, value) => {
    const next = [...windows];
    next[idx] = { ...(next[idx] || {}), [key]: value };
    onChange(next);
  };
  const remove = (idx) => onChange(windows.filter((_, i) => i !== idx));
  const add = () =>
    onChange([...(windows || []), { day: 6, start: "10:00", end: "13:00" }]);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">
          Weekly windows ({windows.length})
        </div>
        <button
          type="button"
          onClick={add}
          className="text-[11px] text-zinc-400 hover:text-white inline-flex items-center gap-1"
          data-testid="avail-window-add"
        >
          <Icons.Plus size={11} />
          Add window
        </button>
      </div>
      {windows.length === 0 && (
        <div className="text-[11px] text-zinc-600 italic px-3 py-3 border border-dashed border-white/10 rounded-md">
          No windows defined yet. The "Book a viewing" CTA will fall back to
          a free-text "preferred time" until you add at least one.
        </div>
      )}
      <div className="space-y-2">
        {windows.map((w, i) => (
          <div
            key={i}
            className="bg-white/[0.03] border border-white/10 rounded-lg p-2 flex items-center gap-2"
            data-testid={`avail-window-${i}`}
          >
            <select
              value={w.day}
              onChange={(e) => update(i, "day", Number(e.target.value))}
              className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none"
              data-testid={`avail-window-${i}-day`}
            >
              {DAY_LABELS.map((d, idx) => (
                <option key={idx} value={idx}>
                  {d}
                </option>
              ))}
            </select>
            <input
              type="time"
              value={w.start || "10:00"}
              onChange={(e) => update(i, "start", e.target.value)}
              className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none"
              data-testid={`avail-window-${i}-start`}
            />
            <span className="text-zinc-600 text-xs">→</span>
            <input
              type="time"
              value={w.end || "13:00"}
              onChange={(e) => update(i, "end", e.target.value)}
              className="bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none"
              data-testid={`avail-window-${i}-end`}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="ml-auto text-zinc-500 hover:text-red-400 p-1.5"
              data-testid={`avail-window-${i}-remove`}
            >
              <Icons.X size={11} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

